import { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ProjectStorage } from '~/types/project';
import { WorksheetField } from '../../../../worksheets/common/components/Field';
import WorksheetForm from '../../../../worksheets/common/components/WorksheetForm';
import StorageFormHeader from './StorageFormHeader';
import { useFadeInStyles } from '~/hooks';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import '../collapsibleCard.scss';

export default function StorageForm({
  storage,
  removeRow,
}: {
  storage: ProjectStorage
  removeRow: (storage: ProjectStorage) => void
}) {
  const [open, setOpen] = useState(true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Returns a schemaKey like storage.0.make for saving the value in the project object
  const schemaKey = (term: string) => `storage.${storage.order}.${term}`;

  const handleRemove = () => {
    removeRow(storage);
    setShowRemoveConfirm(false);
  };

  return (
    <section className="collapsible-card" style={useFadeInStyles()}>
      <Collapsible.Root open={open} onOpenChange={() => setOpen(!open)}>
        <StorageFormHeader
          storage={storage}
          isCollapsed={!open}
          onRemove={() => setShowRemoveConfirm(true)}
        />
        <Collapsible.Content className="CollapsibleContent">
          <WorksheetForm isInner>
            <WorksheetField.Row style={{ marginTop: 'var(--medium)' }}>
              <WorksheetField
                schemaKey={schemaKey('make')}
                fieldLabel="Make"
                placeholder="Enter make"
              />
              <WorksheetField
                schemaKey={schemaKey('model')}
                fieldLabel="Model"
                placeholder="Enter model"
              />
            </WorksheetField.Row>
            <WorksheetField.Row style={{ marginBottom: '1rem' }}>
              <WorksheetField
                type="number"
                fieldLabel="Wattage"
                schemaKey={schemaKey('wattage')}
                placeholder="Enter wattage"
                decimals={0}
                label="kW"
              />
              <WorksheetField
                type="number"
                fieldLabel="Capacity"
                schemaKey={schemaKey('discharge')}
                placeholder="Enter capacity"
                decimals={0}
                label="kWh"
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="number"
                fieldLabel="Warranty term"
                schemaKey={schemaKey('warrantyTerm')}
                placeholder="Enter term"
              />
              <WorksheetField
                fieldLabel="Maintenance provider"
                schemaKey={schemaKey('maintenanceProvider')}
                placeholder="Enter provider"
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                fieldLabel="EMS provider"
                schemaKey={schemaKey('emsProvider')}
                placeholder="Enter provider"
              />
              <WorksheetField
                fieldLabel="Who is responsible for ESS dispatch?"
                schemaKey={schemaKey('essResponsibleParty')}
                placeholder="Enter responsible party"
              />
            </WorksheetField.Row>
          </WorksheetForm>
        </Collapsible.Content>
      </Collapsible.Root>
      {showRemoveConfirm && (
        <Modal closeModal={() => setShowRemoveConfirm(false)}>
          <Modal.Title>Remove BESS {storage.order + 1}?</Modal.Title>
          <Modal.Content>
            <p>
              This will permanently remove this storage system and all of its inputs from the project.
              This action cannot be undone.
            </p>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={() => setShowRemoveConfirm(false)}>
              Cancel
            </Button>
            <Button primary onClick={handleRemove}>
              Remove storage
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </section>
  );
}
